/**
 * VoiceAgentProvider - Simplified context provider using LiveKit high-level hooks
 */

import { useChat, useConnectionState, useLocalParticipant, useRoomContext, useTranscriptions, useVoiceAssistant } from '@livekit/components-react'
import { ConnectionState } from 'livekit-client'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { DEFAULT_CONFIG } from './lib/constants'
import type { AgentState, CallState, Message, VoiceAgentContextValue, VoiceAgentProps, VoiceConnectionState } from './lib/types'
import { generateMessageId } from './lib/utils'

// Create the context
const VoiceAgentContext = createContext<VoiceAgentContextValue | null>(null)

// Provider props
interface VoiceAgentProviderProps extends VoiceAgentProps {
  children: React.ReactNode
  onCallStateChange?: (state: CallState) => void
}

/**
 * VoiceAgentProvider component - Simplified version using LiveKit hooks
 */
export const VoiceAgentProvider: React.FC<VoiceAgentProviderProps> = ({ children, onCallStateChange, ...props }) => {
  // Merge with default config
  const config = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...props,
    }),
    [props],
  )

  // LiveKit hooks
  const room = useRoomContext()
  const connectionState = useConnectionState()
  const { state: agentState, agent: agentParticipant } = useVoiceAssistant()
  const { chatMessages, send: sendChat } = useChat()
  const transcriptions = useTranscriptions()
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant()

  // Local state for additional features
  const [microphoneLevel, setMicrophoneLevel] = useState(0)
  const [availableMicrophones, setAvailableMicrophones] = useState<MediaDeviceInfo[]>([])
  const [selectedMicrophone, setSelectedMicrophone] = useState<string | null>(null)
  const [callState, setCallState] = useState<CallState>('idle')

  // Map LiveKit ConnectionState to our VoiceConnectionState
  const mapConnectionState = (state: ConnectionState): VoiceConnectionState => {
    switch (state) {
      case ConnectionState.Connected:
        return 'connected'
      case ConnectionState.Connecting:
        return 'connecting'
      case ConnectionState.Reconnecting:
        return 'reconnecting'
      case ConnectionState.Disconnected:
        return 'disconnected'
      default:
        return 'disconnected'
    }
  }

  // Merge transcriptions and chat messages into our Message format
  const transcript = useMemo(() => {
    const messages: Message[] = []

    // Convert transcription events to our format
    transcriptions.forEach((transcription) => {
      const isUser = transcription.participantInfo.identity === localParticipant?.identity
      messages.push({
        id: transcription.streamInfo.id,
        role: isUser ? 'user' : 'agent',
        content: transcription.text,
        timestamp: new Date(transcription.streamInfo.timestamp),
        type: 'voice',
      })
    })

    // Convert chat messages to our format
    chatMessages.forEach((msg) => {
      messages.push({
        id: generateMessageId(),
        role: msg.from?.identity === localParticipant?.identity ? 'user' : 'agent',
        content: msg.message,
        timestamp: new Date(msg.timestamp || Date.now()),
        type: 'chat',
      })
    })

    // Sort by timestamp
    return messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }, [transcriptions, chatMessages, localParticipant])

  /**
   * Toggle microphone on/off
   */
  const toggleMicrophone = useCallback(async () => {
    if (!localParticipant) return

    try {
      const newState = !isMicrophoneEnabled
      await localParticipant.setMicrophoneEnabled(newState)
    } catch (error) {
      console.error('Failed to toggle microphone:', error)
      config.onError?.(error as Error)
    }
  }, [localParticipant, isMicrophoneEnabled, config])

  /**
   * Select a different microphone
   */
  const selectMicrophone = useCallback(
    async (deviceId: string) => {
      if (!localParticipant) return

      try {
        setSelectedMicrophone(deviceId)
        // To change microphone, we need to disable and re-enable with new device
        await localParticipant.setMicrophoneEnabled(false)
        await localParticipant.setMicrophoneEnabled(true, { deviceId })
      } catch (error) {
        console.error('Failed to select microphone:', error)
        config.onError?.(error as Error)
      }
    },
    [localParticipant, config],
  )

  /**
   * Send a chat message
   */
  const sendChatMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return

      try {
        await sendChat(content)
        // Call onTranscriptUpdate if provided
        config.onTranscriptUpdate?.(transcript)
      } catch (error) {
        console.error('Failed to send chat message:', error)
        config.onError?.(error as Error)
      }
    },
    [sendChat, transcript, config],
  )

  /**
   * Clear the transcript (local only, doesn't affect actual chat history)
   */
  const clearTranscript = useCallback(() => {
    console.warn('Clear transcript not supported with LiveKit hooks - transcript is managed by LiveKit')
  }, [])

  /**
   * Connect to room - handled by RoomProvider, so this is a no-op
   */
  const connect = useCallback(async () => {
    console.log('Connect is handled by RoomProvider')
  }, [])

  /**
   * Disconnect from room
   */
  const disconnect = useCallback(() => {
    if (room) {
      room.disconnect()
    }
    // Reset call state to idle
    setCallState('idle')
    // Notify parent
    onCallStateChange?.('idle')
  }, [room, onCallStateChange])

  /**
   * Start a call from idle state
   */
  const startCall = useCallback(async () => {
    console.log('[VoiceAgentProvider] startCall() called, callState:', callState)

    if (callState !== 'idle') {
      console.warn('[VoiceAgentProvider] Call already in progress')
      return
    }

    try {
      // Set call state to connecting (this will trigger parent to connect via callback)
      console.log('[VoiceAgentProvider] Setting callState to connecting')
      setCallState('connecting')
      console.log('[VoiceAgentProvider] Calling onCallStateChange with connecting')
      onCallStateChange?.('connecting')
    } catch (error) {
      console.error('[VoiceAgentProvider] Failed to start call:', error)
      config.onError?.(error as Error)
      setCallState('idle')
    }
  }, [callState, onCallStateChange, config])

  /**
   * Update available microphones
   */
  useEffect(() => {
    const updateDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const microphones = devices.filter((d) => d.kind === 'audioinput')
        setAvailableMicrophones(microphones)

        // Select first microphone if none selected
        if (!selectedMicrophone && microphones.length > 0) {
          setSelectedMicrophone(microphones[0].deviceId)
        }
      } catch (error) {
        console.error('Failed to enumerate devices:', error)
      }
    }

    updateDevices()

    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', updateDevices)

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', updateDevices)
    }
  }, [selectedMicrophone])

  /**
   * Monitor microphone level
   */
  useEffect(() => {
    if (!localParticipant || !isMicrophoneEnabled) {
      setMicrophoneLevel(0)
      return
    }

    // Get the audio track
    const audioTrack = Array.from(localParticipant.audioTrackPublications.values())[0]
    if (!audioTrack?.track) return

    // Create audio context for level monitoring
    const audioContext = new AudioContext()
    const analyser = audioContext.createAnalyser()
    const source = audioContext.createMediaStreamSource(new MediaStream([audioTrack.track.mediaStreamTrack]))
    source.connect(analyser)

    const dataArray = new Uint8Array(analyser.frequencyBinCount)
    let animationId: number

    const updateLevel = () => {
      analyser.getByteFrequencyData(dataArray)
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length
      setMicrophoneLevel(average / 255) // Normalize to 0-1

      animationId = requestAnimationFrame(updateLevel)
    }

    updateLevel()

    return () => {
      cancelAnimationFrame(animationId)
      source.disconnect()
      audioContext.close()
    }
  }, [localParticipant, isMicrophoneEnabled])

  // Sync callState with connectionState
  useEffect(() => {
    if (connectionState === ConnectionState.Connected && callState === 'connecting') {
      console.log('[VoiceAgentProvider] Connection established, setting callState to connected')
      setCallState('connected')
      onCallStateChange?.('connected')
    }
    // Don't automatically reset to idle - only reset via explicit disconnect() call
  }, [connectionState, callState, onCallStateChange])

  // Context value
  const value: VoiceAgentContextValue = {
    // Connection
    room: room || null,
    connectionState: mapConnectionState(connectionState),
    connectionDetails: null, // Will be set by the parent component
    agentState: agentState as AgentState,
    callState,

    // Participants
    localParticipant: localParticipant || null,
    agentParticipant: agentParticipant || null,

    // Conversation
    transcript,
    isAgentSpeaking: agentState === 'speaking',
    isUserSpeaking: microphoneLevel > 0.1,

    // Microphone
    isMicrophoneEnabled,
    microphoneLevel,
    availableMicrophones,
    selectedMicrophone,

    // Actions
    connect,
    disconnect,
    startCall,
    toggleMicrophone,
    selectMicrophone,
    sendChatMessage,
    clearTranscript,

    // Config
    config,

    // Debug info
    debugInfo: config.debugMode
      ? {
          latency: 0, // Would need to calculate from room metrics
          connectionQuality: 'good' as const,
          packetsLost: 0,
          jitter: 0,
        }
      : undefined,
  }

  return <VoiceAgentContext.Provider value={value}>{children}</VoiceAgentContext.Provider>
}

/**
 * Hook to use the VoiceAgent context
 */
export const useVoiceAgent = () => {
  const context = useContext(VoiceAgentContext)
  if (!context) {
    throw new Error('useVoiceAgent must be used within a VoiceAgentProvider')
  }
  return context
}

export default VoiceAgentProvider
